<template>
    <nav class="app-nav" :class="{ 'app-nav--mini': mini }" aria-label="Menú principal">
        <ul class="app-nav__list">
            <template v-for="item in nav">
                <li v-if="item.type === 'heading'" :key="item.key" class="app-nav__heading">{{ item.text }}</li>

                <li v-else-if="item.type === 'group'" :key="item.key">
                    <button type="button" class="app-nav__group"
                        :class="{ 'is-current': isCurrentGroup(item) }"
                        :aria-expanded="mini ? null : String(isOpen(item))"
                        :aria-label="mini ? item.text : null" :title="mini ? item.text : null"
                        @click="onGroup(item)">
                        <i :class="item.icon" class="app-nav__icon" aria-hidden="true"></i>
                        <span v-if="!mini" class="app-nav__label">{{ item.text }}</span>
                        <i v-if="!mini" class="fas fa-chevron-down app-nav__chev"
                            :class="{ 'is-open': isOpen(item) }" aria-hidden="true"></i>
                    </button>
                    <ul v-if="!mini && isOpen(item)" class="app-nav__list app-nav__sub">
                        <li v-for="child in item.children" :key="child.key">
                            <router-link class="app-nav__link" active-class="is-active"
                                :to="child.to" :exact="child.exact">
                                <span class="app-nav__label">{{ child.text }}</span>
                                <span v-if="child.isNew" class="app-nav__tag">Nuevo</span>
                                <span v-else-if="child.isUpdated" class="app-nav__dot" title="Actualizado"></span>
                            </router-link>
                        </li>
                    </ul>
                </li>

                <li v-else :key="item.key">
                    <router-link class="app-nav__link" active-class="is-active"
                        :to="item.to" :exact="item.exact"
                        :aria-label="mini ? item.text : null" :title="mini ? item.text : null">
                        <i :class="item.icon" class="app-nav__icon" aria-hidden="true"></i>
                        <span v-if="!mini" class="app-nav__label">{{ item.text }}</span>
                        <template v-if="!mini">
                            <span v-if="item.isNew" class="app-nav__tag">Nuevo</span>
                            <span v-else-if="item.isUpdated" class="app-nav__dot" title="Actualizado"></span>
                        </template>
                    </router-link>
                </li>
            </template>
        </ul>
    </nav>
</template>
<script>
import { activeGroupKey } from '@/service/permiso/menuModel';

/**
 * Lista del menú lateral. HTML plano + `router-link` + clases propias
 * (`.app-nav*`): no depende de `v-list` ni de `v-list-group`, que cambian de
 * API en Vuetify 3. Los colores salen de los tokens `--app-*` de `main.css`.
 *
 * - `mini`: solo iconos (rail). Un clic en un grupo emite `expand`, porque en
 *   el rail no hay dónde desplegar sus hijos.
 * - Abre sola el grupo de la ruta actual; los demás los abre la persona.
 * - Para migrar a Vue Router 4: `exact` desaparece (usar `exact-active-class`)
 *   y `router.resolve(to).route.path` pasa a ser `router.resolve(to).path`.
 */
export default {
    name: 'AppNavList',
    props: {
        nav: { type: Array, default: () => [] },
        mini: { type: Boolean, default: false }
    },
    data() {
        return { openKeys: [] };
    },
    created() {
        this.openActiveGroup();
    },
    watch: {
        nav() {
            this.openActiveGroup();
        },
        '$route.path'() {
            this.openActiveGroup();
        }
    },
    methods: {
        activeKey() {
            return activeGroupKey(this.nav, this.$route.path, (to) => this.$router.resolve(to).route.path);
        },
        openActiveGroup() {
            const key = this.activeKey();
            if (key && !this.openKeys.includes(key)) this.openKeys.push(key);
        },
        isOpen(group) {
            return this.openKeys.includes(group.key);
        },
        isCurrentGroup(group) {
            return this.activeKey() === group.key;
        },
        onGroup(group) {
            if (this.mini) {
                this.$emit('expand');
                return;
            }
            this.openKeys = this.isOpen(group)
                ? this.openKeys.filter((key) => key !== group.key)
                : [...this.openKeys, group.key];
        }
    }
};
</script>
<style scoped>
.app-nav {
    padding: 8px;
}
.app-nav__list {
    list-style: none;
    margin: 0;
    padding: 0;
}
.app-nav__heading {
    padding: 14px 12px 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--app-text-muted);
}
.app-nav__link,
.app-nav__group {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 40px;
    margin-top: 2px;
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--app-nav-text);
    font: inherit;
    font-size: 14px;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
}
.app-nav__link:hover,
.app-nav__group:hover {
    background: var(--app-nav-hover);
}
.app-nav__link:focus-visible,
.app-nav__group:focus-visible {
    outline: 2px solid var(--app-primary);
    outline-offset: -2px;
}
/* activo: píldora blanca sobre el salvia, filete fino y barra de color a la izquierda */
.app-nav__link.is-active {
    background: var(--app-surface);
    box-shadow: inset 3px 0 0 var(--app-primary), 0 0 0 1px var(--app-line);
    color: var(--app-primary);
    font-weight: 600;
}
/* grupo: fila con icono y chevron; sus hijos cuelgan de una línea guía */
.app-nav__group {
    font-weight: 600;
}
.app-nav__sub {
    margin-left: 21px;
    padding-left: 6px;
    border-left: 1px solid var(--app-line-strong);
}
.app-nav__sub .app-nav__link {
    min-height: 34px;
    padding-left: 12px;
}
.app-nav__group.is-current {
    color: var(--app-text);
}
.app-nav__icon {
    flex: 0 0 18px;
    width: 18px;
    font-size: 15px;
    text-align: center;
}
.app-nav__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.app-nav__chev {
    margin-left: auto;
    font-size: 10px;
    transition: transform .15s;
}
.app-nav__chev.is-open {
    transform: rotate(180deg);
}
.app-nav__tag {
    margin-left: auto;
    padding: 1px 8px;
    border-radius: 99px;
    background: var(--app-primary-soft);
    color: var(--app-primary);
    font-size: 11px;
    font-weight: 600;
}
.app-nav__dot {
    margin-left: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--app-success);
}

/* rail: solo iconos centrados */
.app-nav--mini {
    padding: 8px 6px;
}
.app-nav--mini .app-nav__link,
.app-nav--mini .app-nav__group {
    justify-content: center;
    min-height: 40px;
    margin-top: 2px;
    padding: 0;
    font-size: 14px;
}
.app-nav--mini .app-nav__group.is-current {
    background: var(--app-surface);
    box-shadow: 0 0 0 1px var(--app-line);
    color: var(--app-primary);
}
</style>
